import api from './api';

/**
 * GK-1 — region grape rules and the consistency findings they produce
 * (docs/GRAPE-KNOWLEDGE-DESIGN §2, §4).
 *
 * The API owns truth here as everywhere: this screen edits rule PERIODS and
 * reads back the EFFECTIVE rule the server resolved, rather than resolving
 * inheritance itself. A curator must never see one answer here and another
 * in the checker.
 */

export type RuleMode = 'exclusive' | 'restricted' | 'open' | 'unknown';

export interface ColourRule {
  mode: RuleMode;
  grapes: string[];
  notes?: string;
}

export interface RulePeriod {
  effectiveFrom: number | null;
  effectiveTo: number | null;
  byColour: Record<string, ColourRule>;
  source?: { kind?: string; ref?: string; quote?: string };
}

export interface RuleContribution {
  regionId: string;
  regionName: string;
  mode: RuleMode;
  grapes: string[];
  periodRef: string | null;
  isSelf: boolean;
}

export interface EffectiveRule {
  colour: string;
  mode: RuleMode;
  grapes: string[];
  regionId: string | null;
  regionName: string | null;
  periodRef: string | null;
  via: RuleContribution[];
  sources: { regionName: string; ref?: string | null; quote?: string | null }[];
}

/** §2.5 — what this region states, and what actually applies after inheritance. */
export interface VarietalMinimum {
  own: number | null;
  effectivePct: number | null;
  setBy: string | null;
  inherited: boolean;
}

export interface RegionGrapeRules {
  region: {
    _id: string;
    name: string;
    hierarchyNames: string[];
    varietalLabellingMinimumPct: number | null;
  };
  varietalMinimum: VarietalMinimum;
  periods: RulePeriod[];
  /** False when the region has never had a rule recorded — the honest default. */
  seeded: boolean;
  ancestors: { _id: string; name: string }[];
  effectiveByColour: EffectiveRule[];
  grapeNames: Record<string, string>;
  vintage: number | null;
}

/** §2.4 — the write-time refusal, already phrased for a human. */
export interface RuleWidensConflict {
  colour: string;
  grapes: string[];
  grapeNames: string[];
  ancestorRegion: string;
  permitted: string[];
  permittedNames: string[];
  sentence: string;
}

export interface SweepResult {
  created: number;
  updated: number;
  reopened: number;
  unchanged: number;
  resolved: number;
  regionsInSubtree: number;
  wineNamesChecked: number;
}

export interface GrapeFinding {
  _id: string;
  key: string;
  kind: 'CONTRADICTION' | 'COVERAGE' | 'PRECISION_INSUFFICIENT';
  code: string;
  subjectKind: 'wineName' | 'wine';
  subjectId: string;
  regionId: string | null;
  rulePeriodRef: string | null;
  reason: string;
  details?: Record<string, unknown> & {
    rule?: { mode: RuleMode; grapes: string[]; via: RuleContribution[]; sources: unknown[] };
  };
  inputHash: string;
  status: 'open' | 'dismissed';
  createdAt: string;
}

export interface FindingSubject {
  displayName: string;
  color: string;
  region: string | null;
  labelGrapes: { id: string; name: string }[];
}

export interface FindingsPage {
  items: GrapeFinding[];
  total: number;
  subjects: Record<string, FindingSubject>;
  /** Names for every grape id the page renders, the rule's included. */
  grapeNames: Record<string, string>;
  counts: { contradictions: number; coverage: number; precision: number };
}

export interface CoverageRow {
  regionId: string;
  name: string;
  hierarchyString: string;
  wineNames: number;
  coloursRecorded: string[];
}

export interface CoverageWorklist {
  items: CoverageRow[];
  totalRegions: number;
  regionsWithRules: number;
}

/** §6 — a display layer as the API resolved it at read time. */
export interface DisplayLayer {
  layer: 'actual' | 'characteristic' | 'declared';
  text: string;
  badge: string;
  precision: 'exact' | 'composition' | 'dominant' | 'unknown' | 'declared';
  grapes: { grapeId: string | null; text: string; pct?: number }[];
  isApproximation: boolean;
}

export interface BlendDisplay {
  layers: DisplayLayer[];
  best: DisplayLayer | null;
  /** Hedged when approximate; null when it must not be shown at all. */
  guestLine: string | null;
  /** Present on a wine name: the actuals live on the vintages (§3.2). */
  actualBlendsLiveOnVintages?: true;
}

const grapeRulesService = {
  async getRegionRules(regionId: string, vintage?: number | null): Promise<RegionGrapeRules> {
    const res = await api.get(`/admin/wine-region/${regionId}/grape-rules`, {
      params: vintage ? { vintage } : undefined,
    });
    return res.data;
  },

  /**
   * Saves the region's periods. A 409 RULE_WIDENS_ANCESTOR is not an error
   * to swallow — it is the curator-facing block, and the caller shows its
   * conflicts verbatim.
   */
  async putRegionRules(
    regionId: string,
    periods: RulePeriod[],
    /** null clears an override so the region falls back to inheritance. */
    varietalLabellingMinimumPct?: number | null,
  ): Promise<{ sweep: SweepResult }> {
    const res = await api.put(`/admin/wine-region/${regionId}/grape-rules`, {
      periods,
      ...(varietalLabellingMinimumPct !== undefined ? { varietalLabellingMinimumPct } : {}),
    });
    return res.data;
  },

  async listFindings(params: {
    kind?: string; status?: string; regionId?: string; subjectId?: string; limit?: number;
  } = {}): Promise<FindingsPage> {
    const res = await api.get('/admin/grape-findings', { params });
    return res.data;
  },

  async checkNow(body: { wineNameId?: string; wineId?: string; vintage?: number | null; store?: boolean }) {
    const res = await api.post('/admin/grape-findings/check', body);
    return res.data;
  },

  async dismiss(findingId: string, reason: string): Promise<GrapeFinding> {
    const res = await api.post(`/admin/grape-findings/${findingId}/dismiss`, { reason });
    return res.data;
  },

  async blendDisplay(params: { wineId?: string; wineNameId?: string }): Promise<BlendDisplay> {
    const res = await api.get('/admin/grape-findings/blend-display', { params });
    return res.data;
  },

  async coverage(limit = 40): Promise<CoverageWorklist> {
    const res = await api.get('/admin/grape-findings/coverage', { params: { limit } });
    return res.data;
  },
};

export default grapeRulesService;
